import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';

import {
  StoreUtil,
} from 'doctivity-shared/utils';

import {
  TableWithDialog,
  LoadingView,
  AdminToolsTabs,
} from 'components';

import {
  queryLoginSettings,
  upsertLoginSettings,
} from 'store/actions/loginSettingsActions';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
});

const schema = {
  type: 'object',
  required: ['email_domain'], // Removed 'login_type' from required
  properties: {
    email_domain: { 
      type: 'string', 
      title: 'Email Domain',
      description: 'Domain without @ symbol (e.g., company.com)'
    },
    separator: {
      type: 'null',
      title: ''
    },
    login_type: {
      type: 'string',
      title: '', // Empty title removes the label
      enum: ['DOCTIVITY_LOGIN', 'SAML2'],
      enumNames: ['Doctivity Login', 'SAML2'],
      default: 'DOCTIVITY_LOGIN'
    }
  },
  allOf: [
    {
      if: {
        properties: { login_type: { const: 'DOCTIVITY_LOGIN' } }
      },
      then: {
        properties: {
          failed_login_attempts: { 
            type: 'number', 
            title: 'Failed Login Attempts',
            minimum: 1,
            default: 5
          },
          failed_login_timeout: { 
            type: 'number', 
            title: 'Failed Login Timeout (seconds)',
            minimum: 1,
            default: 300
          },
          two_factor_bypass_time: { 
            type: 'number', 
            title: 'Two Factor Bypass Time (hours)',
            minimum: 0,
            default: 24
          },
          password_length: { 
            type: 'number', 
            title: 'Minimum Password Length', 
            default: 8, 
            minimum: 8 
          }
        }
      }
    },
    {
      if: {
        properties: { login_type: { const: 'SAML2' } }
      },
      then: {
        properties: {
          sso_login_url: {
            type: 'string',
            title: 'SSO Login URL',
            format: 'uri',
            description: 'SAML Identity Provider SSO URL'
          },
          sso_logout_url: {
            type: 'string',
            title: 'SSO Logout URL',
            format: 'uri',
            description: 'SAML Identity Provider Logout URL'
          },
          certificate: {
            type: 'string',
            title: 'Certificate',
            description: 'X.509 Certificate from Identity Provider (PEM format)'
          }
        },
        required: ['sso_login_url', 'sso_logout_url', 'certificate']
      }
    }
  ]
};

const uiSchema = {
  email_domain: {
    'ui:order': 1
  },
  // Add a dummy field just for the separator line
  separator: {
    'ui:field': () => React.createElement('hr', { style: { margin: '20px 0 10px 0', border: '1px solid #ccc' } }),
    'ui:order': 1.5
  },
  login_type: {
    'ui:widget': 'radio',
    'ui:options': {
      inline: true,
      // Try to override the radio button styling directly
      props: {
        color: 'default',
        style: { color: '#666' }
      }
    },
    'ui:order': 2
  },
  // Doctivity Login fields
  failed_login_attempts: {
    'ui:classNames': 'two-column-field',
    'ui:order': 3
  },
  failed_login_timeout: {
    'ui:classNames': 'two-column-field',
    'ui:order': 4
  },
  two_factor_bypass_time: {
    'ui:classNames': 'two-column-field',
    'ui:order': 5
  },
  password_length: {
    'ui:classNames': 'two-column-field',
    'ui:order': 6
  },
  // SAML2 fields
  sso_login_url: {
    'ui:help': ' ',
    'ui:placeholder': 'https://accounts.google.com/o/saml2/idp?idpid=...',
    'ui:order': 7
  },
  sso_logout_url: {
    'ui:help': ' ',
    'ui:placeholder': 'https://accounts.google.com/logout',
    'ui:order': 8
  },
  certificate: {
    'ui:help': 'Paste the X.509 certificate from your Identity Provider',
    'ui:placeholder': '-----BEGIN CERTIFICATE-----\nMIIC...\n-----END CERTIFICATE-----',
    'ui:order': 9
  }
};

// this controls which columns are displayed and how they are looked up in data
const columns = [
  {
    label: 'Email Domain',
    key: 'email_domain',
  },
  {
    label: 'Login Type',
    key: 'login_type',
    render: (value) => value === 'SAML2' ? 'SAML2' : 'Doctivity Login'
  },
  {
    label: 'Failed Attempts',
    key: 'failed_login_attempts',
  },
  {
    label: 'Timeout (sec)',
    key: 'failed_login_timeout',
  },
  {
    label: '2FA Bypass (hrs)',
    key: 'two_factor_bypass_time',
  },
  {
    label: 'Min Password',
    key: 'password_length',
  }
];

class LoginSettingsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      query: {
        order: [
          ['email_domain', 'ASC'],
        ],
        limit: 25,
        offset: 0,
      },
    };

    this.onSave = this.onSave.bind(this);
    this.onQueryChange = this.onQueryChange.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.renderMetadataUrl = this.renderMetadataUrl.bind(this);
  }

  componentDidMount() {
    const {
      loginSettings: loginSettings,
    } = this.props;

    console.log('LoginSettings data on mount:', loginSettings);

    if (StoreUtil.needsLoadNoCache(loginSettings)) {
      console.log('Fetching login settings data...');
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps) {
    // is object getting edited?
    if (this.props.editing !== prevProps.editing) {
      // if so and it is newer than the list, update list
      if (StoreUtil.hasSavedSinceLoad(this.props.editing, this.props.loginSettings)) {
        this.fetchData();
      }
    }

    // Debug: Log when loginSettings data changes
    if (this.props.loginSettings !== prevProps.loginSettings) {
      console.log('LoginSettings data updated:', this.props.loginSettings);
      // Log individual records to see what fields they have
      if (this.props.loginSettings && this.props.loginSettings.rows) {
        console.log('Sample record fields:', Object.keys(this.props.loginSettings.rows[0] || {}));
        console.log('First record data:', this.props.loginSettings.rows[0]);
      }
    }
  }

  validateForm(formData, errors) {
    // Custom validation for SAML fields
    if (formData.login_type === 'SAML2') {
      if (!formData.sso_login_url) {
        errors.sso_login_url = { __errors: ['SSO Login URL is required for SAML2'] };
      }
      if (!formData.sso_logout_url) {
        errors.sso_logout_url = { __errors: ['SSO Logout URL is required for SAML2'] };
      }
      if (!formData.certificate) {
        errors.certificate = { __errors: ['Certificate is required for SAML2'] };
      }
    }
    return errors;
  }

  onSave(data) {
    const {
      dispatch,
    } = this.props;

    // For SAML configurations, still generate certificate_path for potential file backup
    if (data.login_type === 'SAML2' && data.certificate) {
      const certificatePath = `./config/saml/${data.email_domain.replace(/\./g, '_')}_${data.environment}.crt`;
      data.certificate_path = certificatePath;
    }

    dispatch(upsertLoginSettings(data));
  }

  onQueryChange(query) {
    this.setState({ query }, this.fetchData.bind(this));
  }

  fetchData() {
    const {
      dispatch,
    } = this.props;

    const {
      query,
    } = this.state;

    console.log('Fetching data with query:', query);
    dispatch(queryLoginSettings(query));
  }

  renderMetadataUrl(formData) {
    if (formData.login_type === 'SAML2' && formData.email_domain) {
      // Use NODE_ENV directly in the webdomain string
      const webdomain = window.location.origin;
      
      const domain = formData.email_domain;
      const metadataUrl = `${webdomain}/api/auth/saml/metadata/${domain}`;
      
      return (
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <strong>SAML Metadata URL:</strong><br/>
          <a href={metadataUrl} target='_blank' rel='noopener noreferrer' style={{ wordBreak: 'break-all' }}>
            {metadataUrl}
          </a>
          <br/>
          <small>Use this URL to configure your Identity Provider</small>
        </div>
      );
    }
    return null;
  }

  render() {
    const {
      loginSettings: loginSettings,
      editing,
      classes,
    } = this.props;

    const {
      query,
    } = this.state;

    // is a user being edited now?
    if (StoreUtil.isSaving(editing)) {
      return (<LoadingView />);
    }

    return (
      <>
        <AdminToolsTabs tab='login_settings' />
        <div className={classes.container}>
          <TableWithDialog
            title='Login Settings'
            dialogTitle='Login Setting'
            columns={columns}
            dialogSchema={schema}
            dialogUISchema={uiSchema}
            query={query}
            data={loginSettings}
            onQueryChange={this.onQueryChange}
            onSave={this.onSave}
            formValidate={this.validateForm}
            showFilters={false}
            validateOnChange={true}
            dialogAdditionalContent={this.renderMetadataUrl}
          />
        </div>
      </>
    );
  }
}

LoginSettingsPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  loginSettings: PropTypes.object.isRequired,
  editing: PropTypes.object,
};

function mapStateToProps(state) {
  const {
    loginSettings: loginSettings,
  } = state;

  return {
    loginSettings: StoreUtil.get(loginSettings, StoreUtil.COMMON_TABLE),
    editing: StoreUtil.get(loginSettings, StoreUtil.COMMON_EDIT_ITEM),
  };
}

const styled = withStyles(styles)(LoginSettingsPage);
const connected = connect(mapStateToProps)(styled);
export { connected as LoginSettingsPage };